/**
 * useChatSessions — 聊天会话管理 Hook
 *
 * 职责：
 * - 从后端加载会话列表（getSessionList）
 * - 创建新会话（createSession）
 * - 删除会话（deleteSession）
 * - 切换当前活跃会话
 * - 自动将活跃会话 ID 持久化到 localStorage
 */

import { useState, useEffect, useCallback } from 'react';
import { chatAPI } from '@/service/chat';
import type { ChatSessionRecord, ChatSessionListData } from '@/service/chat';

/** useChatSessions 返回值类型 */
export interface UseChatSessionsReturn {
  /** 会话列表 */
  sessions: ChatSessionRecord[];
  /** 当前活跃会话 ID */
  activeSessionId: number | null;
  /** 设置活跃会话 */
  setActiveSessionId: (id: number | null) => void;
  /** 加载中标记 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 刷新会话列表 */
  refresh: () => Promise<void>;
  /** 创建新会话，返回新会话 ID，失败返回 null */
  createSession: (title?: string) => Promise<number | null>;
  /** 删除会话，成功返回 true */
  deleteSession: (sessionId: number) => Promise<boolean>;
}

const ACTIVE_SESSION_KEY = 'ad_blog_chat_active_session_id';

/**
 * 管理聊天会话的 Hook
 * @param user_id 用户 ID（可选，默认 0）
 */
export const useChatSessions = (user_id?: number): UseChatSessionsReturn => {
  const [sessions, setSessions] = useState<ChatSessionRecord[]>([]);
  const [activeSessionId, setActiveSessionIdState] = useState<number | null>(() => {
    // 初始化时从 localStorage 恢复
    const saved = localStorage.getItem(ACTIVE_SESSION_KEY);
    return saved ? Number(saved) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 加载会话列表
   */
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await chatAPI.getSessionList({ userId: user_id });
      if (res.success) {
        setSessions(res.data.list);
        // 如果当前活跃会话已被删除，清空
        setActiveSessionIdState((prev) => {
          if (prev !== null && !res.data.list.some((s) => s.id === prev)) {
            return null;
          }
          return prev;
        });
      } else {
        setError(res.message || '获取会话列表失败');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取会话列表失败');
    } finally {
      setLoading(false);
    }
  }, [user_id]);

  /** 组件挂载时加载会话列表 */
  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * 持久化活跃会话 ID
   */
  const setActiveSessionId = useCallback((id: number | null) => {
    setActiveSessionIdState(id);
    if (id !== null) {
      localStorage.setItem(ACTIVE_SESSION_KEY, String(id));
    } else {
      localStorage.removeItem(ACTIVE_SESSION_KEY);
    }
  }, []);

  /**
   * 创建新会话
   * @param title 会话标题（可选）
   * @returns 新会话 ID，失败返回 null
   */
  const createSession = useCallback(
    async (title?: string): Promise<number | null> => {
      setError(null);
      try {
        const res = await chatAPI.createSession(title, user_id);
        if (res.success) {
          // 将新会话添加到列表头部
          setSessions((prev) => [res.data, ...prev]);
          return res.data.id;
        } else {
          setError(res.message || '创建会话失败');
          return null;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '创建会话失败');
        return null;
      }
    },
    [user_id]
  );

  /**
   * 删除会话
   * @param sessionId 会话 ID
   * @returns 是否删除成功
   */
  const deleteSession = useCallback(
    async (sessionId: number): Promise<boolean> => {
      setError(null);
      try {
        const res = await chatAPI.deleteSession(sessionId);
        if (res.success) {
          setSessions((prev) => prev.filter((s) => s.id !== sessionId));
          // 如果删除的是当前活跃会话，清空
          setActiveSessionIdState((prev) => (prev === sessionId ? null : prev));
          return true;
        } else {
          setError(res.message || '删除会话失败');
          return false;
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '删除会话失败');
        return false;
      }
    },
    []
  );

  return {
    sessions,
    activeSessionId,
    setActiveSessionId,
    loading,
    error,
    refresh,
    createSession,
    deleteSession,
  };
};
